// File: src/pages/HomePage.js
// This file is updated to fix the incorrect image URLs.
import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

function HomePage() {
  return (
    <>
      <Header />
      <main>
        {/* Hero Section */}
        <section className="hero-bg min-h-screen flex items-center">
            <div className="container mx-auto px-6 text-center">
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-black uppercase">
                    <span className="text-magenta">Rise</span> And <span className="text-lime">Win</span>
                </h1>
                <p className="text-lg md:text-2xl mt-4 max-w-3xl mx-auto">
                    Your journey to a stronger, healthier you starts now. Unleash your potential with R.A.W. Fitness.
                </p>
                <div className="mt-8">
                    <a href="#features" className="text-black font-bold py-4 px-8 rounded-lg bg-lime hover:bg-opacity-80 transition duration-300 text-xl glow-lime">Start Your Journey</a>
                </div>
            </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20">
            <div className="container mx-auto px-6">
                <h2 className="text-4xl font-bold text-center mb-12">Why <span className="text-magenta">R.A.W.</span> Fitness?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div className="bg-gray-900 p-8 rounded-lg border-t-4 border-magenta text-center transform hover:-translate-y-2 transition-transform duration-300">
                        <h3 className="text-2xl font-bold mb-4 text-magenta">Progress Tracking</h3>
                        <p>Log your workouts, track your stats, and visualize your progress with our intuitive user dashboard. See how far you've come and stay motivated.</p>
                    </div>
                    <div className="bg-gray-900 p-8 rounded-lg border-t-4 border-lime text-center transform hover:-translate-y-2 transition-transform duration-300">
                        <h3 className="text-2xl font-bold mb-4 text-lime">Custom Programs</h3>
                        <p>Get personalized running and workout plans generated just for you. Whether you're a beginner or a pro, we have a program to match your goals.</p>
                    </div>
                    <div className="bg-gray-900 p-8 rounded-lg border-t-4 border-magenta text-center transform hover:-translate-y-2 transition-transform duration-300">
                        <h3 className="text-2xl font-bold mb-4 text-magenta">Inspirational Content</h3>
                        <p>Stay inspired with a constant stream of motivational videos, articles, and success stories from the R.A.W. Fitness community.</p>
                    </div>
                </div>
            </div>
        </section>

        {/* News & Videos Section */}
        <section id="news" className="py-20 bg-gray-900">
            <div className="container mx-auto px-6">
                <h2 className="text-4xl font-bold text-center mb-12">Latest <span className="text-lime">News</span> & Videos</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div className="bg-black rounded-lg overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                        <img src="https://placehold.co/600x400/F500F5/FFFFFF?text=Video+Thumbnail" alt="Video or News Thumbnail" className="w-full h-48 object-cover" />
                        <div className="p-6">
                            <h3 className="text-2xl font-bold mb-3 text-magenta">New 10-Minute HIIT Workout</h3>
                            <p className="text-gray-400 mb-4">Short on time? This high-intensity workout will get your heart pumping and torch calories in just 10 minutes.</p>
                            <Link to="/articles/new-10-minute-hiit-workout" className="text-lime font-bold hover:underline">Watch Now &rarr;</Link>
                        </div>
                    </div>
                    <div className="bg-black rounded-lg overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                        <img src="https://placehold.co/600x400/82F52C/000000?text=Article+Image" alt="Video or News Thumbnail" className="w-full h-48 object-cover" />
                        <div className="p-6">
                            <h3 className="text-2xl font-bold mb-3 text-lime">5 Healthy Meal Prep Ideas</h3>
                            <p className="text-gray-400 mb-4">Fail to plan, plan to fail. Get ahead of your week with these five simple and delicious meal prep recipes.</p>
                            <Link to="/articles/5-healthy-meal-prep-ideas" className="text-lime font-bold hover:underline">Read More &rarr;</Link>
                        </div>
                    </div>
                    <div className="bg-black rounded-lg overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                        <img src="https://placehold.co/600x400/F500F5/FFFFFF?text=Video+Thumbnail" alt="Video or News Thumbnail" className="w-full h-48 object-cover" />
                        <div className="p-6">
                            <h3 className="text-2xl font-bold mb-3 text-magenta">Yoga for Beginners</h3>
                            <p className="text-gray-400 mb-4">Find your flow and improve your flexibility with this guided yoga session designed for absolute beginners.</p>
                            <Link to="/articles/yoga-for-beginners" className="text-lime font-bold hover:underline">Watch Now &rarr;</Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Calculators Section */}
        <section id="calculators" className="py-20">
            <div className="container mx-auto px-6 text-center">
                <h2 className="text-4xl font-bold text-center mb-12">Fitness <span className="text-magenta">Calculators</span></h2>
                <p className="max-w-2xl mx-auto mb-12">Quickly calculate key health metrics to better understand your body and tailor your fitness plan.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="bg-gray-900 p-6 rounded-lg">
                        <h3 className="text-xl font-bold text-lime mb-2">BMI Calculator</h3>
                        <p>Calculate your Body Mass Index.</p>
                    </div>
                    <div className="bg-gray-900 p-6 rounded-lg">
                        <h3 className="text-xl font-bold text-lime mb-2">BMR Calculator</h3>
                        <p>Determine your Basal Metabolic Rate.</p>
                    </div>
                    <div className="bg-gray-900 p-6 rounded-lg">
                        <h3 className="text-xl font-bold text-lime mb-2">Calorie Calculator</h3>
                        <p>Estimate your daily calorie needs.</p>
                    </div>
                </div>
                <div className="mt-8">
                    <a href="#calculators" className="text-black font-bold py-3 px-6 rounded-lg bg-lime hover:bg-opacity-80 transition duration-300 glow-lime">Use Calculators</a>
                </div>
            </div>
        </section>

        {/* About Us Section */}
        <section id="about" className="py-20 bg-gray-900">
            <div className="container mx-auto px-6">
                <div className="flex flex-col md:flex-row items-center gap-12">
                    <div className="md:w-1/2">
                        <img src="https://placehold.co/600x400/82F52C/000000?text=Our+Mission" alt="About R.A.W. Fitness" className="rounded-lg shadow-lg w-full" />
                    </div>
                    <div className="md:w-1/2">
                        <h2 className="text-4xl font-bold mb-4">Our Mission is to <span className="text-magenta">Inspire</span></h2>
                        <p className="mb-4">We believe that everyone has the power to transform their life through fitness. R.A.W. Fitness was born from a passion for helping people break through their limits and "Rise and Win" every single day. We're not just a website; we're a community dedicated to empowerment, health, and active living.</p>
                        <p>Join us to get access to the tools, motivation, and support you need to achieve your personal best.</p>
                    </div>
                </div>
            </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20">
            <div className="container mx-auto px-6">
                <h2 className="text-4xl font-bold text-center mb-12">Get In <span className="text-lime">Touch</span></h2>
                <div className="max-w-2xl mx-auto">
                    <form action="#" method="POST">
                        <div className="flex flex-wrap -mx-3 mb-6">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-xs font-bold mb-2" htmlFor="grid-first-name">
                                    First Name
                                </label>
                                <input className="appearance-none block w-full bg-gray-800 text-white border border-gray-700 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-gray-700 focus:border-lime" id="grid-first-name" type="text" placeholder="Jane" />
                            </div>
                            <div className="w-full md:w-1/2 px-3">
                                <label className="block uppercase tracking-wide text-xs font-bold mb-2" htmlFor="grid-last-name">
                                    Last Name
                                </label>
                                <input className="appearance-none block w-full bg-gray-800 text-white border border-gray-700 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-700 focus:border-lime" id="grid-last-name" type="text" placeholder="Doe" />
                            </div>
                        </div>
                        <div className="flex flex-wrap -mx-3 mb-6">
                            <div className="w-full px-3">
                                <label className="block uppercase tracking-wide text-xs font-bold mb-2" htmlFor="grid-email">
                                    Email Address
                                </label>
                                <input className="appearance-none block w-full bg-gray-800 text-white border border-gray-700 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-gray-700 focus:border-lime" id="grid-email" type="email" placeholder="email@example.com" />
                            </div>
                        </div>
                        <div className="flex flex-wrap -mx-3 mb-6">
                            <div className="w-full px-3">
                                <label className="block uppercase tracking-wide text-xs font-bold mb-2" htmlFor="grid-message">
                                    Message
                                </label>
                                <textarea className="appearance-none block w-full bg-gray-800 text-white border border-gray-700 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-gray-700 focus:border-lime h-48 resize-none" id="grid-message"></textarea>
                            </div>
                        </div>
                        <div className="flex justify-center">
                            <button className="bg-magenta text-black font-bold py-3 px-8 rounded-lg hover:bg-opacity-80 transition duration-300 glow-magenta" type="button">
                                Send Message
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default HomePage;
